import { RULE_TYPES, emptyRule, typeOfApi } from './campaign-rule';
import { arrayToMenuIds, menuIdsToArray } from '../components/coupon-menus';

// Kiosk 0, Online/QR 10, Mobile 20 — the order every row draws them in.
export const CHANNEL_ORDER = ['0', '10', '20'];

// `platforms` is a CSV; null or blank means every channel (spec §2.2). The
// api also accepts a JSON list (`["0","10"]`: PublicCouponAvailability.cs
// strips `[`, `]` and `"`), and a value that names no channel means none.
export const platformsFromCsv = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return [...CHANNEL_ORDER];
  const parts = String(value)
    .replace(/[[\]"\s]/g, '')
    .split(',');
  return CHANNEL_ORDER.filter((channel) => parts.includes(channel));
};

export const platformsToCsv = (list) => CHANNEL_ORDER.filter((channel) => (list || []).includes(channel)).join(',');

// A PublicCouponModel (a List row) as the modals' form. The window
// (`startsat`, `endsat`) rides along untouched: Upsert assigns both from the
// body on update. A free item's product comes from `freeitem.productid`
// when the row carries it; the Edit modal otherwise reads it off
// ProductChoices by `freeitemid`.
export const formFromCoupon = (coupon) => {
  const type = typeOfApi(coupon?.type) || 'discount';
  const rule = {
    ...emptyRule(type),
    minSpend: typeof coupon?.minprice === 'number' ? coupon.minprice : null,
    amount: type === 'discount' ? coupon?.discountamount ?? null : null,
    unit: coupon?.discounttype === 0 ? 'percent' : 'fixed',
    productId: type === 'buy_x_get_y' ? coupon?.getProductItemId || null : coupon?.freeitem?.productid || null,
    itemId: type === 'free_item' ? coupon?.freeitemid || null : null,
    buyQty: type === 'buy_x_get_y' ? coupon?.buyItemCount ?? null : null,
    getQty: type === 'buy_x_get_y' ? coupon?.getItemCount ?? null : null,
  };
  return {
    id: coupon?.id || null,
    companyid: coupon?.companyid || null,
    name: coupon?.name || '',
    code: coupon?.code || '',
    rule,
    storeid: coupon?.storeid || null,
    platforms: platformsFromCsv(coupon?.platforms),
    // The stored value as the api holds it: a save that leaves the channels
    // alone sends it back, so a JSON list or an odd value is not rewritten.
    platformsRaw: coupon?.platforms ?? null,
    menuids: menuIdsToArray(coupon?.menuids),
    status: typeof coupon?.status === 'number' ? coupon.status : 0,
    startsat: coupon?.startsat ?? null,
    endsat: coupon?.endsat ?? null,
  };
};

// The form as PublicCoupon/Upsert's body. Fields the type does not use are
// sent null, as the old modal's buildBody did.
const sameChannels = (a, b) => a.length === b.length && a.every((channel) => b.includes(channel));

// A stored value is only worth sending back when rewriting it would change
// what the api holds: a JSON list, or a value that names no channel. A plain
// CSV of channels (or null) is the same list in any order, so it is written
// in the canonical order, as every body was before.
const PLAIN_CHANNEL_CSV = /^\s*(0|10|20)(\s*,\s*(0|10|20))*\s*$/;
const isPlainChannelCsv = (raw) => raw === null || String(raw).trim() === '' || PLAIN_CHANNEL_CSV.test(String(raw));

const trimmed = (value) => (typeof value === 'string' ? value.trim() : value);

export const upsertBody = (form, { companyid, id } = {}) => {
  const { rule } = form;
  const keepStored =
    form.platformsRaw !== undefined &&
    !isPlainChannelCsv(form.platformsRaw) &&
    sameChannels(form.platforms || [], platformsFromCsv(form.platformsRaw));
  return {
    id: id || null,
    name: trimmed(form.name),
    code: trimmed(form.code),
    companyid,
    storeid: form.storeid || null,
    type: RULE_TYPES[rule.type],
    minprice: rule.minSpend ?? 0,
    discountamount: rule.type === 'discount' ? rule.amount : null,
    discounttype: rule.type === 'discount' ? (rule.unit === 'percent' ? 0 : 1) : null,
    freeitemid: rule.type === 'free_item' ? rule.itemId : null,
    getProductItemId: rule.type === 'buy_x_get_y' ? rule.productId : null,
    buyItemCount: rule.type === 'buy_x_get_y' ? rule.buyQty : null,
    getItemCount: rule.type === 'buy_x_get_y' ? rule.getQty : null,
    platforms: keepStored ? form.platformsRaw : platformsToCsv(form.platforms),
    menuids: arrayToMenuIds(form.menuids),
    status: form.status,
    startsat: form.startsat ?? null,
    endsat: form.endsat ?? null,
  };
};
